import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useBuilderInspect,
  useDetectTemplate,
  useNameExists,
  useSaveScraper,
  useTestScraper,
  type PreviewRaffle,
} from '../../api/wizard';
import { useScrapers } from '../../api/queries';
import { useCloneConfig } from '../../api/wizard';
import { Ic } from '../../components/Ic';
import { Field } from '../../components/Field';
import { Switch } from '../../components/Switch';
import { toast } from '../../components/Toast';
import { dateTime } from '../../lib/time';
import {
  DEFAULT_CUSTOM_CODE,
  INTERVALS,
  TEMPLATES,
  TEMPLATE_FIELDS,
  type TemplateId,
} from './templates';

type ConfigValue = string | boolean;

const STEPS = ['Source', 'Configure', 'Test & save'] as const;

export function AddScraperView() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const step = Math.min(2, Math.max(0, Number(params.get('step') ?? 0) || 0));
  const setStep = (n: number) => {
    const next = new URLSearchParams(params);
    next.set('step', String(n));
    setParams(next, { replace: true });
  };

  const [url, setUrl] = useState('');
  const [detectFor, setDetectFor] = useState('');
  const [template, setTemplate] = useState<TemplateId | null>(null);
  const [name, setName] = useState('');
  const [siteName, setSiteName] = useState('');
  const [interval, setInterval] = useState(600);
  const [config, setConfig] = useState<Record<string, ConfigValue>>({});
  const [code, setCode] = useState(DEFAULT_CUSTOM_CODE);
  const [preview, setPreview] = useState<PreviewRaffle[] | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const { data: scrapers } = useScrapers();
  const detection = useDetectTemplate(detectFor, !!detectFor);
  const nameCheck = useNameExists(name);
  const inspect = useBuilderInspect();
  const clone = useCloneConfig();
  const test = useTestScraper();
  const save = useSaveScraper();

  const fields = template ? TEMPLATE_FIELDS[template] : [];

  const configJson = useMemo(() => {
    const out: Record<string, unknown> = {};
    if (siteName.trim()) out.site_name = siteName.trim();
    for (const f of fields) {
      const v = config[f.id];
      if (f.type === 'checkbox') out[f.id] = !!v;
      else if (typeof v === 'string' && v.trim()) out[f.id] = v.trim();
    }
    return out;
  }, [config, fields, siteName]);

  const missingRequired = fields.filter((f) => f.required && !String(config[f.id] ?? '').trim());
  const nameValid = /^[a-zA-Z0-9_-]+$/.test(name);
  const nameTaken = nameCheck.data?.exists ?? false;
  const canConfigure = !!template;
  const canTest = canConfigure && nameValid && !nameTaken && missingRequired.length === 0;

  const runInspect = async () => {
    const target = String(config.page_url ?? '') || String(config.base_url ?? '') || url;
    if (!target) return toast('Enter a page URL to inspect', 'error');
    try {
      const result = await inspect.mutateAsync(target);
      if (result.error) return toast(result.error, 'error');
      setConfig((prev) => ({ ...prev, ...(result.suggested_config as Record<string, ConfigValue>) }));
      if (result.suggested_name && !name) setName(result.suggested_name);
      if (result.suggested_site_name && !siteName) setSiteName(result.suggested_site_name);
      toast(`Inspected ${result.title ?? target}`, 'success');
    } catch (e) {
      toast(`Inspect failed: ${e instanceof Error ? e.message : String(e)}`, 'error');
    }
  };

  const runTest = async () => {
    setTestError(null);
    setPreview(null);
    try {
      const result = await test.mutateAsync({
        template: template!,
        name,
        code,
        config_json: configJson,
      });
      if (result.error) setTestError(result.error);
      else setPreview(result.raffles ?? []);
    } catch (e) {
      setTestError(e instanceof Error ? e.message : String(e));
    }
  };

  const runSave = async () => {
    try {
      await save.mutateAsync({
        template: template!,
        name,
        code,
        config_json: configJson,
        interval_seconds: interval,
      });
      toast(`Scraper "${name}" saved and scheduled`, 'success');
      navigate('/scrapers');
    } catch (e) {
      toast(`Save failed: ${e instanceof Error ? e.message : String(e)}`, 'error');
    }
  };

  return (
    <div className="view-pad" style={{ maxWidth: 940 }}>
      <div className="sec-head">
        <h2>Add a scraper</h2>
        <span className="desc">detect a template, configure it, then dry-run before saving</span>
      </div>

      {/* step rail */}
      <div className="chip-row" style={{ marginBottom: 18 }}>
        {STEPS.map((label, i) => (
          <button
            key={label}
            className={`chip ${step === i ? 'on' : ''}`}
            disabled={(i === 1 && !canConfigure) || (i === 2 && !canTest)}
            onClick={() => setStep(i)}
          >
            {i + 1}. {label}
          </button>
        ))}
      </div>

      {/* ── Step 1: source ─────────────────────────────────────────────── */}
      {step === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="pcard">
            <div className="head">
              <Ic.search className="ic" />
              <h3>Detect from a URL</h3>
            </div>
            <div className="body pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="input"
                  placeholder="https://store.example.com/raffles"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && setDetectFor(url.trim())}
                />
                <button
                  className="btn primary"
                  disabled={!url.trim() || detection.isFetching}
                  onClick={() => setDetectFor(url.trim())}
                >
                  {detection.isFetching ? 'Detecting…' : 'Detect'}
                </button>
              </div>

              {detection.data && (
                <div
                  className={detection.data.template === 'custom' ? 'err' : 'keyval'}
                  style={{ maxWidth: '100%' }}
                >
                  {detection.data.error ? (
                    <span>{detection.data.error} — pick a template manually below.</span>
                  ) : (
                    <span>
                      Detected <strong>{detection.data.template}</strong> (
                      {detection.data.confidence} confidence)
                      {detection.data.reason ? ` · ${detection.data.reason}` : ''}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="pcard">
            <div className="head">
              <Ic.box className="ic" />
              <h3>Template</h3>
            </div>
            <div className="body pad">
              <div className="scraper-grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))' }}>
                {TEMPLATES.map((t) => {
                  const suggested = detection.data?.template === t.id;
                  return (
                    <button
                      key={t.id}
                      className="hchip"
                      style={
                        template === t.id
                          ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)' }
                          : undefined
                      }
                      onClick={() => {
                        setTemplate(t.id);
                        if (url && !config.base_url) {
                          setConfig((prev) => ({ ...prev, base_url: url.trim() }));
                        }
                        setStep(1);
                      }}
                    >
                      <div className="row1">
                        <span className="name">{t.label}</span>
                        {suggested && <span className="pill green">suggested</span>}
                      </div>
                      <div className="site">{t.blurb}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="pcard">
            <div className="head">
              <Ic.copy className="ic" />
              <h3>Or clone an existing scraper</h3>
            </div>
            <div className="body pad" style={{ display: 'flex', gap: 8 }}>
              <select
                className="input"
                defaultValue=""
                onChange={async (e) => {
                  const from = e.target.value;
                  if (!from) return;
                  const cfg = await clone.mutateAsync(from);
                  if (cfg.template && cfg.template !== 'custom') setTemplate(cfg.template as TemplateId);
                  if (cfg.interval_seconds) setInterval(cfg.interval_seconds);
                  if (cfg.config_json) setConfig(cfg.config_json as Record<string, ConfigValue>);
                  toast(`Copied settings from ${from}`, 'success');
                  setStep(1);
                }}
              >
                <option value="">Choose a scraper…</option>
                {scrapers?.map((s) => (
                  <option key={s.name} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 2: configure ──────────────────────────────────────────── */}
      {step === 1 && template && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="pcard">
            <div className="head">
              <Ic.edit className="ic" />
              <h3>Identity</h3>
              <span className="spacer" />
              <span className="pill indigo">{template}</span>
            </div>
            <div className="body pad form-grid">
              <Field label="Scraper name" hint="letters, numbers, dash, underscore">
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value.trim())}
                  placeholder="nikeau"
                />
              </Field>
              <Field label="Site name" hint="shown in the Discord embed">
                <input
                  className="input"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="Nike AU"
                />
              </Field>
              <div style={{ gridColumn: '1 / -1' }}>
                {name && !nameValid && (
                  <div className="err">Only letters, numbers, dashes and underscores.</div>
                )}
                {name && nameValid && nameTaken && (
                  <div className="err">
                    "{name}" already exists ({nameCheck.data?.source}). Choose another.
                  </div>
                )}
                {name && nameValid && !nameTaken && nameCheck.data && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    "{name}" is available.
                  </div>
                )}
              </div>
              <Field label="Poll interval">
                <select
                  className="input"
                  value={interval}
                  onChange={(e) => setInterval(Number(e.target.value))}
                >
                  {INTERVALS.map((i) => (
                    <option key={i.value} value={i.value}>
                      Every {i.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          {template === 'custom' ? (
            <div className="pcard">
              <div className="head">
                <Ic.code className="ic" />
                <h3>Scraper source</h3>
              </div>
              <div className="body pad">
                <textarea
                  className="input code"
                  spellCheck={false}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  style={{ minHeight: 360, whiteSpace: 'pre', overflowX: 'auto' }}
                />
              </div>
            </div>
          ) : (
            <div className="pcard">
              <div className="head">
                <Ic.settings className="ic" />
                <h3>Configuration</h3>
                {template === 'guided' && (
                  <>
                    <span className="spacer" />
                    <button className="btn sm" disabled={inspect.isPending} onClick={runInspect}>
                      <Ic.search className="ico" />
                      {inspect.isPending ? 'Inspecting…' : 'Inspect page'}
                    </button>
                  </>
                )}
              </div>
              <div className="body pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {fields.map((f) =>
                  f.type === 'checkbox' ? (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Switch
                        on={!!config[f.id]}
                        onChange={(next) => setConfig((prev) => ({ ...prev, [f.id]: next }))}
                        label={f.label}
                      />
                      <div>
                        <div style={{ fontSize: 13 }}>{f.label}</div>
                        <div className="muted" style={{ fontSize: 11.5 }}>
                          {f.help}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Field
                      key={f.id}
                      label={`${f.label}${f.required ? ' *' : ''}`}
                      hint={f.help}
                    >
                      <input
                        className="input mono-sm"
                        placeholder={f.placeholder}
                        value={String(config[f.id] ?? '')}
                        onChange={(e) => setConfig((prev) => ({ ...prev, [f.id]: e.target.value }))}
                      />
                    </Field>
                  ),
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn ghost" onClick={() => setStep(0)}>
              <Ic.chevLeft className="ico" /> Back
            </button>
            <span style={{ flex: 1 }} />
            {missingRequired.length > 0 && (
              <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
                Required: {missingRequired.map((f) => f.label).join(', ')}
              </span>
            )}
            <button className="btn primary" disabled={!canTest} onClick={() => setStep(2)}>
              Test it <Ic.arrowRight className="ico" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: test & save ────────────────────────────────────────── */}
      {step === 2 && template && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="pcard">
            <div className="head">
              <Ic.zap className="ic" />
              <h3>Dry run</h3>
              <span className="desc muted">nothing is sent to Discord</span>
              <span className="spacer" />
              <button className="btn primary sm" disabled={test.isPending} onClick={runTest}>
                {test.isPending ? 'Running…' : 'Run test'}
              </button>
            </div>
            <div className="body pad">
              {!preview && !testError && !test.isPending && (
                <div className="muted" style={{ fontSize: 12.5 }}>
                  Run the scraper once against the live site to see what it would find.
                </div>
              )}
              {testError && <div className="err">{testError}</div>}
              {preview && preview.length === 0 && (
                <div className="err">
                  The scraper ran but found no raffles. Check your selectors, or the site may have
                  nothing live right now.
                </div>
              )}
              {preview && preview.length > 0 && (
                <>
                  <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
                    Found {preview.length} raffle{preview.length > 1 ? 's' : ''}.
                  </div>
                  <div className="tbl-scroll">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Price</th>
                          <th>Closes</th>
                          <th>Link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.map((r, i) => (
                          <tr key={i}>
                            <td>{r.product ?? '—'}</td>
                            <td className="mono-sm">{r.price != null ? `$${r.price}` : '—'}</td>
                            <td className="mono-sm">
                              {r.subscription_cutoff_time
                                ? dateTime(r.subscription_cutoff_time)
                                : '—'}
                            </td>
                            <td className="mono-sm" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.link ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="pcard">
            <div className="head">
              <Ic.check className="ic" />
              <h3>Summary</h3>
            </div>
            <div className="body pad">
              <table className="assign">
                <tbody>
                  <tr>
                    <td>Name</td>
                    <td className="mono-sm">{name}</td>
                  </tr>
                  <tr>
                    <td>Template</td>
                    <td className="mono-sm">{template}</td>
                  </tr>
                  <tr>
                    <td>Interval</td>
                    <td className="mono-sm">
                      {INTERVALS.find((i) => i.value === interval)?.label ?? `${interval}s`}
                    </td>
                  </tr>
                  <tr>
                    <td>Config</td>
                    <td>
                      <pre className="mono-sm" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(configJson, null, 2)}
                      </pre>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn ghost" onClick={() => setStep(1)}>
              <Ic.chevLeft className="ico" /> Back
            </button>
            <span style={{ flex: 1 }} />
            {!preview?.length && (
              <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>
                Saving without a successful test is allowed, but not advised.
              </span>
            )}
            <button className="btn primary" disabled={save.isPending || !canTest} onClick={runSave}>
              <Ic.upload className="ico" /> {save.isPending ? 'Saving…' : 'Save & schedule'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
